import React from "react";
import { Dialog, DialogContent } from "@mui/material";
import {
  Button,
  Iconography,
  IconographySizes,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import "../../partner-organizations/manage-partner/unsaved-changes/UnsavedChangesModal.scss";
import { getRolePermissionDetailSagaAction } from "../../../../../redux/saga/user/saga-actions";
import { RolePermissionItem } from "../../../../../redux/slices/user/Types";
import { RoleCardData } from "../data/mockRoles";
import { SETTINGS_PERMISSION_KEYS } from "../../../../../utils/permissions/settingsPermissionKeys";
import { PARTNER_PORTAL_PERMISSION_KEYS } from "../../../../../utils/permissions/partnerPortalPermissionKeys";

function isSuperAdminRoleType(role: RoleCardData): boolean {
  return role.roleType.trim().toLowerCase() === "super admin";
}

function isUserManagementPermission(
  permissionKey: string,
  permissionLabel: string
): boolean {
  const key = permissionKey.trim().toLowerCase();
  const label = permissionLabel.trim().toLowerCase();
  return (
    key === SETTINGS_PERMISSION_KEYS.USER_SETTINGS ||
    label === "user management"
  );
}

function isPartnerPortalDashboardPermission(permissionKey: string): boolean {
  return (
    permissionKey.trim().toLowerCase() ===
    PARTNER_PORTAL_PERMISSION_KEYS.PARTNER_PORTAL_DASHBOARD
  );
}

function normalizePermissionAction(
  action: string | undefined | null
): "edit" | "view" | "na" {
  const normalized = String(action ?? "")
    .trim()
    .toLowerCase();
  if (normalized === "edit") return "edit";
  if (normalized === "view") return "view";
  return "na";
}

function resolvePermissionAction(
  permissionKey: string,
  action: "edit" | "view" | "na"
): "edit" | "view" | "na" {
  if (isPartnerPortalDashboardPermission(permissionKey) && action === "na") {
    return "view";
  }
  return action;
}

interface RolePermissionsDetailProps {
  role: RoleCardData;
  onBack: () => void;
  onSave: (payload: {
    role_id: number;
    permissions: Array<{ permission_id: number; action: string }>;
  }) => void;
  readOnly?: boolean;
}

const PermissionRowControl: React.FC<{
  groupId: string;
  rowId: string;
  value: "edit" | "view" | "na";
  onChange: (value: "edit" | "view" | "na") => void;
  readOnly?: boolean;
  disableNa?: boolean;
}> = ({ groupId, rowId, value, onChange, readOnly = false, disableNa = false }) => {
  const radioName = `${groupId}-${rowId}`;
  return (
    <>
      <label className="rp-radio-cell">
        <input
          type="radio"
          name={radioName}
          checked={value === "edit"}
          disabled={readOnly}
          onChange={() => onChange("edit")}
        />
      </label>
      <label className="rp-radio-cell">
        <input
          type="radio"
          name={radioName}
          checked={value === "view"}
          disabled={readOnly}
          onChange={() => onChange("view")}
        />
      </label>
      <label className="rp-radio-cell">
        <input
          type="radio"
          name={radioName}
          checked={value === "na"}
          disabled={readOnly || disableNa}
          onChange={() => onChange("na")}
        />
      </label>
    </>
  );
};

const RolePermissionsDetail: React.FC<RolePermissionsDetailProps> = ({
  role,
  onBack,
  onSave,
  readOnly = false,
}) => {
  const [activePortal, setActivePortal] = React.useState<"admin" | "partner">(
    "admin"
  );
  const [searchText, setSearchText] = React.useState("");
  const dispatch = useAppDispatch();
  const { rolePermissionDetail, status } = useAppSelector((state) => state.user);

  React.useEffect(() => {
    if (role.apiId > 0) {
      dispatch(getRolePermissionDetailSagaAction({ roleID: role.apiId }));
    }
  }, [dispatch, role.apiId]);

  React.useEffect(() => {
    const hasAdmin =
      rolePermissionDetail?.data?.admin &&
      Object.keys(rolePermissionDetail.data.admin).length > 0;
    const hasPartner =
      rolePermissionDetail?.data?.partner &&
      Object.keys(rolePermissionDetail.data.partner).length > 0;

    if (!hasAdmin && hasPartner) {
      setActivePortal("partner");
    } else {
      setActivePortal("admin");
    }
  }, [rolePermissionDetail]);

  const portalData = React.useMemo(() => {
    if (!rolePermissionDetail?.data) {
      return {};
    }
    return rolePermissionDetail.data[activePortal] || {};
  }, [rolePermissionDetail, activePortal]);

  const permissionGroups = React.useMemo(() => {
    return Object.entries(portalData).map(([title, rows]) => ({
      id: `${activePortal}-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
      title,
      rows: (rows || []).map((row: RolePermissionItem) => ({
        id: String(row.permission_id),
        permissionKey: row.permission_key,
        label: row.permission_label,
        value:
          resolvePermissionAction(
            row.permission_key,
            normalizePermissionAction(row.action)
          ),
      })),
    }));
  }, [portalData, activePortal]);

  const allPermissions = React.useMemo(() => {
    const adminEntries = Object.values(rolePermissionDetail?.data?.admin || {}).flat();
    const partnerEntries = Object.values(
      rolePermissionDetail?.data?.partner || {}
    ).flat();
    return [...adminEntries, ...partnerEntries] as RolePermissionItem[];
  }, [rolePermissionDetail]);

  const [actionByPermissionId, setActionByPermissionId] = React.useState<
    Record<number, "edit" | "view" | "na">
  >({});
  const [initialActionByPermissionId, setInitialActionByPermissionId] =
    React.useState<Record<number, "edit" | "view" | "na">>({});

  React.useEffect(() => {
    const nextState: Record<number, "edit" | "view" | "na"> = {};
    allPermissions.forEach((item) => {
      const normalized = resolvePermissionAction(
        item.permission_key,
        normalizePermissionAction(item.action)
      );
      nextState[item.permission_id] = normalized;
    });
    setActionByPermissionId(nextState);
    setInitialActionByPermissionId(nextState);
  }, [allPermissions]);

  const [expandedGroupIds, setExpandedGroupIds] = React.useState<string[]>([]);
  const [revertConfirmOpen, setRevertConfirmOpen] = React.useState(false);

  const hasUnsavedPermissionChanges = React.useMemo(() => {
    const currentIds = Object.keys(actionByPermissionId);
    const initialIds = Object.keys(initialActionByPermissionId);
    if (currentIds.length !== initialIds.length) return true;
    return currentIds.some(
      (id) =>
        actionByPermissionId[Number(id)] !==
        initialActionByPermissionId[Number(id)]
    );
  }, [actionByPermissionId, initialActionByPermissionId]);

  const handleCancelClick = () => {
    if (readOnly || !hasUnsavedPermissionChanges) return;
    setRevertConfirmOpen(true);
  };

  const handleConfirmRevert = () => {
    setActionByPermissionId(initialActionByPermissionId);
    setRevertConfirmOpen(false);
  };

  const filteredGroups = React.useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return permissionGroups;

    return permissionGroups
      .map((group) => ({
        ...group,
        rows: group.rows.filter((row) =>
          row.label.toLowerCase().includes(query)
        ),
      }))
      .filter((group) => group.rows.length > 0);
  }, [permissionGroups, searchText]);

  React.useEffect(() => {
    setExpandedGroupIds(permissionGroups.map((group) => group.id));
  }, [permissionGroups]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroupIds((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  const totals = React.useMemo(() => {
    const values = Object.values(actionByPermissionId);
    const edit = values.filter((value) => value === "edit").length;
    const view = values.filter((value) => value === "view").length;
    const na = values.filter((value) => value === "na").length;
    return {
      edit,
      view,
      na,
      total: values.length,
    };
  }, [actionByPermissionId]);

  const handleSavePermissions = () => {
    if (role.apiId <= 0) {
      return;
    }
    const lockUserManagement = isSuperAdminRoleType(role);
    const permissions = Object.entries(actionByPermissionId).map(
      ([permissionId, action]) => {
        const permissionIdNum = Number(permissionId);
        const item = allPermissions.find(
          (p) => p.permission_id === permissionIdNum
        );
        const isLockedRow =
          lockUserManagement &&
          item &&
          isUserManagementPermission(
            item.permission_key,
            item.permission_label
          );
        const effectiveAction = isLockedRow
          ? initialActionByPermissionId[permissionIdNum] ?? action
          : item
            ? resolvePermissionAction(item.permission_key, action)
            : action;
        return {
          permission_id: permissionIdNum,
          action: effectiveAction === "na" ? "NA" : effectiveAction,
        };
      }
    );
    onSave({
      role_id: role.apiId,
      permissions,
    });
  };

  const hasAdminTab = Boolean(
    rolePermissionDetail?.data?.admin &&
      Object.keys(rolePermissionDetail.data.admin).length > 0
  );
  const hasPartnerTab = Boolean(
    rolePermissionDetail?.data?.partner &&
      Object.keys(rolePermissionDetail.data.partner).length > 0
  );

  return (
    <section className="rp-detail">
      <div className="rp-detail__topbar">
        <button type="button" className="rp-detail__back" onClick={onBack}>
          <Iconography icon="arrowLeft" size={IconographySizes.Small} />
          Back
        </button>
        <div className="rp-detail__actions">
          <div className="rp-detail__search">
            <Iconography
              icon="magnifyingGlass"
              size={IconographySizes.Small}
              className="rp-detail__search-icon"
            />
            <input
              type="text"
              placeholder="Search Feature"
              value={searchText}
              onChange={(e) => setSearchText(e.currentTarget.value)}
            />
          </div>
          <button
            type="button"
            className="rp-detail__cancel"
            onClick={handleCancelClick}
            disabled={readOnly || !hasUnsavedPermissionChanges}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rp-detail__save"
            onClick={handleSavePermissions}
            disabled={readOnly}
          >
            Save
          </button>
        </div>
      </div>

      <div className="rp-detail__rolecard">
        <div className="rp-detail__roleinfo">
          <div className="rp-detail__lock">
            <Iconography icon="lock" size={IconographySizes.Medium} />
          </div>
          <div>
            <h3>{role.name}</h3>
            <p>View or make changes to this role&apos;s permissions</p>
          </div>
        </div>
        <div className="rp-detail__stats">
          <span className="rp-chip rp-chip--edit">Edit: {totals.edit}</span>
          <span className="rp-chip rp-chip--view">View: {totals.view}</span>
          <span className="rp-chip rp-chip--na">NA: {totals.na}</span>
        </div>
      </div>

      <div className="rp-detail__tabs">
        {hasAdminTab && (
          <button
            type="button"
            className={activePortal === "admin" ? "active" : ""}
            onClick={() => setActivePortal("admin")}
          >
            Admin Portal
          </button>
        )}
        {hasPartnerTab && (
          <button
            type="button"
            className={activePortal === "partner" ? "active" : ""}
            onClick={() => setActivePortal("partner")}
          >
            Partner Portal
          </button>
        )}
      </div>

      <div className="rp-detail__table">
        <div className="rp-detail__thead">
          <div>Feature / Permission</div>
          <div>Edit</div>
          <div>View</div>
          <div>NA</div>
        </div>

        {status.rolePermissionDetailLoading ? (
          <div className="rp-detail__empty">Loading permissions...</div>
        ) : filteredGroups.length === 0 ? (
          <div className="rp-detail__empty">No matching features found.</div>
        ) : (
          filteredGroups.map((group) => (
            <div key={group.id} className="rp-detail__group">
              <button
                type="button"
                className="rp-detail__group-header"
                onClick={() => toggleGroup(group.id)}
              >
                <div className="rp-detail__group-title">
                  <Iconography
                    icon={
                      expandedGroupIds.includes(group.id)
                        ? "chevronDown"
                        : "chevronRight"
                    }
                    size={IconographySizes.Small}
                  />
                  <span>{group.title}</span>
                </div>
                <div />
                <div />
                <div />
              </button>
              {expandedGroupIds.includes(group.id) &&
                group.rows.map((row) => {
                  const rowLocked =
                    isSuperAdminRoleType(role) &&
                    isUserManagementPermission(
                      row.permissionKey,
                      row.label
                    );
                  const controlReadOnly = readOnly || rowLocked;
                  const disableNa = isPartnerPortalDashboardPermission(
                    row.permissionKey
                  );

                  return (
                    <div
                      key={row.id}
                      className={`rp-detail__row${
                        rowLocked ? " rp-detail__row--locked" : ""
                      }`}
                    >
                      <div className="rp-detail__feature-name">{row.label}</div>
                      <PermissionRowControl
                        groupId={group.id}
                        rowId={row.id}
                        value={
                          actionByPermissionId[Number(row.id)] || row.value
                        }
                        readOnly={controlReadOnly}
                        disableNa={disableNa}
                        onChange={(nextValue) => {
                          if (rowLocked) return;
                          const permissionId = Number(row.id);
                          setActionByPermissionId((prev) => ({
                            ...prev,
                            [permissionId]: resolvePermissionAction(
                              row.permissionKey,
                              nextValue
                            ),
                          }));
                        }}
                      />
                    </div>
                  );
                })}
            </div>
          ))
        )}
      </div>

      <div className="rp-detail__footer">
        <p>
          <strong>{totals.edit}</strong> of {totals.total} edit permissions enabled for{" "}
          <span>{role.name}</span>
        </p>
        <div>
          <button
            type="button"
            className="rp-detail__cancel"
            onClick={handleCancelClick}
            disabled={readOnly || !hasUnsavedPermissionChanges}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rp-detail__save"
            onClick={handleSavePermissions}
            disabled={readOnly}
          >
            Save
          </button>
        </div>
      </div>

      <Dialog
        open={revertConfirmOpen}
        onClose={() => setRevertConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <div className="bl-unsaved-modal">
          <header className="bl-unsaved-modal-header">
            <div className="bl-unsaved-heading">
              <span className="bl-unsaved-icon" aria-hidden="true">
                <Iconography
                  icon="circleExclamation"
                  size={IconographySizes.Small}
                />
              </span>
              <h3>Unsaved changes</h3>
            </div>
          </header>
          <DialogContent className="bl-unsaved-modal-content">
            <p>
              You have made changes that have not been saved. If you continue,
              your changes will be reverted.
            </p>
            <p>Are you sure you want to continue?</p>
          </DialogContent>
          <footer className="bl-unsaved-modal-footer">
            <Button
              variant="secondary"
              label="CANCEL"
              onClick={() => setRevertConfirmOpen(false)}
            />
            <Button
              variant="primary"
              label="CONTINUE"
              onClick={handleConfirmRevert}
            />
          </footer>
        </div>
      </Dialog>
    </section>
  );
};

export default RolePermissionsDetail;
